<template>
  <sf-text-field
    v-model.number="amount"
    class="field"
    hide-details
    single-line
    :disabled="disabled"
    :append-inner-icon="mdiPlus"
    :prepend-inner-icon="mdiMinus"
    :error="error"
    autocomplete="off"
    :aria-label="$t('punchout.table.quantity')"
    @click:prepend-inner="decrement()"
    @click:append-inner="increment()"
    @change="handleInput()"
    @blur="onBlur"
    @focus="[$event.target.select(), $emit('focus'), (blur = false)]"
    @keydown.enter="$event.target.blur()"
  />
</template>

<script setup lang="ts">
import SfTextField from '@/components/input/SfTextField.vue'
import helpers from '@/modules/message/helpers'
import useDebounce from '@/store/useDebounce'
import { PackagingModel } from '@/types/index'
import { mdiMinus, mdiPlus } from '@mdi/js'
import { storeToRefs } from 'pinia'
import { computed, PropType, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

defineOptions({
  name: 'QuantityStepper'
})

const props = defineProps({
  quantity: {
    type: Number,
    required: true
  },
  stock: {
    type: Number,
    default: 0
  },
  packaging: {
    type: Object as PropType<PackagingModel | undefined>,
    default: undefined
  },
  disabled: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits<{
  changeQuantity: [quantity: number]
  blur: []
  focus: []
}>()

const { t } = useI18n()
const blur = ref(true)
const { timerStarted } = storeToRefs(useDebounce())

const minQuantity = computed(() => {
  if (props.packaging) {
    if (props.packaging.minQuantity > 1) {
      return props.packaging.minQuantity
    } else if (props.packaging.intervalQuantity > 1) {
      return props.packaging.intervalQuantity
    } else {
      return 1
    }
  } else {
    return 1
  }
})

const interval = computed(() => {
  if (props.packaging) {
    if (props.packaging.intervalQuantity > 1) {
      return props.packaging.intervalQuantity
    } else {
      return 1
    }
  } else {
    return 1
  }
})

const amount = ref(props.quantity >= minQuantity.value ? props.quantity : minQuantity.value)
const lastValid = ref(amount.value)
const error = computed(() => props.stock != 0 && props.quantity > props.stock)

watch(
  () => props.quantity,
  (selection) => {
    amount.value = selection
    lastValid.value = selection
  }
)

const isValidQuantity = (quantity: number) => {
  if (quantity < minQuantity.value) {
    return false
  }
  if (quantity % interval.value != 0) {
    return false
  }
  return true
}

// Debounce the quantity changes before emitting
const debounceTime = 1000
let timeoutId: ReturnType<typeof setTimeout> | null = null

const setQuantity = (quantity: number) => {
  if (lastValid.value != quantity) {
    lastValid.value = quantity
    if (timeoutId) clearTimeout(timeoutId)
    timerStarted.value = true
    timeoutId = setTimeout(() => {
      emit('changeQuantity', quantity)
      if (blur.value) {
        emit('blur')
      }
      timerStarted.value = false
    }, debounceTime)
  }
}

const handleInput = () => {
  const quantity = Number(amount.value)
  if (!Number.isNaN(quantity)) {
    if (quantity >= 1) {
      if (isValidQuantity(quantity)) {
        setQuantity(quantity)
      } else {
        amount.value = lastValid.value
      }
    } else {
      helpers.reportError(t('error.positiveDigit'))
      amount.value = lastValid.value
    }
  } else {
    helpers.reportError(t('error.validDigit'))
    amount.value = lastValid.value
  }
}

const increment = () => {
  amount.value += interval.value
  setQuantity(amount.value)
}

const decrement = () => {
  let newQuantity
  if (amount.value <= minQuantity.value) {
    newQuantity = minQuantity.value
  } else {
    amount.value -= interval.value
    newQuantity = amount.value
  }
  setQuantity(newQuantity)
}

const onBlur = () => {
  blur.value = true
  if (!timerStarted.value) {
    emit('blur')
  }
}
</script>

<style lang="scss" scoped>
.field {
  width: 115px;
  max-width: 115px;

  &:deep(input) {
    text-align: center;
  }
}
</style>
